import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import {RootState} from '@/app/store';
import {HttpMethod} from '@/constants/httpMethod';
import {EReducersPath} from '@/dataSource/utils/ReducerPaths';

import {setBearerTokenIfExists, setWafHeaders} from '../headers';
import {reAuth} from '../reAuth';

import {apiBaseAddKid} from './constants';
import {
  INewTeenViaInvitationRequest,
  IRequestCreateKid,
  IRequestCreateSwitchKid,
  IResponseAddKid,
} from './entities';

const baseQuery = async (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    timeout: 30 * 1000,
    prepareHeaders: async (headers, {getState}) => {
      const token = (getState() as RootState).tokenDataSlice?.tokenData
        ?.accessToken;
      setBearerTokenIfExists(headers, token);
      await setWafHeaders(baseUrl, headers);
      return headers;
    },
  });

const baseApiQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const baseUrl = apiBaseAddKid.baseUrl;

  const result = await reAuth(args, api, extraOptions, baseUrl, baseQuery);
  return result;
};

export const addKidApi = createApi({
  reducerPath: EReducersPath.ADD_KID_API,
  refetchOnReconnect: true,
  baseQuery: baseApiQueryWithReAuth,
  endpoints: build => ({
    newKid: build.mutation<IResponseAddKid, IRequestCreateKid>({
      query: body => ({
        url: apiBaseAddKid.endpoints.newTeen,
        method: HttpMethod.POST,
        body,
      }),
    }),
    newSwitchKid: build.mutation<IResponseAddKid, IRequestCreateSwitchKid>({
      query: body => ({
        url: apiBaseAddKid.endpoints.newSwitchTeen,
        method: HttpMethod.POST,
        body,
      }),
    }),
    addTeenWithInvitation: build.mutation<void, INewTeenViaInvitationRequest>({
      query: body => ({
        url: apiBaseAddKid.endpoints.addTeenWithInvitation,
        method: HttpMethod.POST,
        body,
      }),
    }),
  }),
});

export const {
  useNewKidMutation,
  useNewSwitchKidMutation,
  useAddTeenWithInvitationMutation,
} = addKidApi;
